import { memo, useState } from "react";

import { ContentCard, Row } from "@narmi/design_system";
import { useLocalStorage } from "usehooks-ts";
import cc from "classcat";
import { Localized, useLocalization } from "@fluent/react";

import DashboardIcon from "../components/DashboardIcon";

import { DAYS_UNTIL_CONFIRMATION_REQUESTED_AGAIN } from "./constants";
import styles from "./SidebarUpdateCard.module.scss";
import SidebarUpdateCardDialog from "./SidebarUpdateCardDialog";

import type { SidebarUpdateCardProps } from "./sidebar.update.card.types";

const SidebarUpdateCard = ({ isCollapsed }: SidebarUpdateCardProps) => {
  const [isOpen, setOpen] = useState(false);
  const { l10n } = useLocalization();
  const [dismissed, setDismissed] = useLocalStorage<
    { dismissedUntil: string; dismissedCount: number } | undefined
  >("sidebarUpdateCardDismissedUntil", undefined);
  const [isCardConfirmed] = useLocalStorage<boolean>(
    "sidebarUpdateCardConfirmed",
    false
  );

  // They closed the card via X, we'll show it to them again, but not for a few days,
  // then if they close it again, we never show it again.
  function closedSidebarUpdateCard() {
    const date = new Date();
    date.setDate(date.getDate() + DAYS_UNTIL_CONFIRMATION_REQUESTED_AGAIN);
    setDismissed({
      dismissedUntil: date.toString(),
      dismissedCount: (dismissed?.dismissedCount ?? 0) + 1,
    });
  }

  // If they've dismissed the card within N days, don't show it
  const isDismissedRecently =
    dismissed &&
    new Date(dismissed.dismissedUntil).getTime() > new Date().getTime();
  // If they've dismissed the card twice, don't show it
  const isDismissedTwice = dismissed && dismissed.dismissedCount > 1;

  const isHidden =
    isCollapsed || isCardConfirmed || isDismissedRecently || isDismissedTwice;

  return (
    <div
      className={cc([
        styles["sidebar-update-card-wrapper"],
        isHidden && styles["sidebar-update-card-wrapper--hidden"],
      ])}
    >
      <SidebarUpdateCardDialog
        isOpen={isOpen}
        closeDialog={() => setOpen(false)}
      />
      <ContentCard kind="bordered" paddingSize="none">
        <div
          data-testid="sidebar-update-card"
          className={cc([
            styles["sidebar-update-card"],
            isCollapsed && styles["sidebar-update-card--collapsed"],
          ])}
        >
          <button
            type="button"
            className={styles["sidebar-update-card-dismiss"]}
            onClick={closedSidebarUpdateCard}
            aria-label={l10n.getString("update-card-dismiss")}
            data-testid="sidebar-update-card-dismiss"
          >
            <span className="narmi-icon-x" />
          </button>
          <Row alignItems="center" justifyContent="space-between">
            <Row.Item shrink className={styles["sidebar-update-card-chip"]}>
              <span aria-label={l10n.getString("update-card-new", null, "New")}>
                <Localized id="update-card-new">New!</Localized>
              </span>
            </Row.Item>
          </Row>
          <h4 className="nds-sans">
            <Localized id="update-card-title">New side menu</Localized>
          </h4>
          <div>
            <p className="nds-body-s">
              <Localized id="update-card-body">
                We&apos;ve moved your menu to give you more room to manage your
                money.
              </Localized>
            </p>
            <button
              type="button"
              data-testid="sidebar-update-card-learn-more"
              className={styles["sidebar-update-card-cta"]}
              onClick={() => setOpen(true)}
              aria-label={l10n.getString(
                "update-card-cta",
                null,
                "Learn more about the new side menu"
              )}
              aria-haspopup="dialog"
            >
              <Localized id="update-card-cta">Learn more</Localized>
            </button>
          </div>
          <div
            className={styles["sidebar-update-card-spacer"]}
            aria-hidden="true"
          />
          <DashboardIcon className={styles["sidebar-update-card-img"]} />
        </div>
      </ContentCard>
    </div>
  );
};

export default memo(SidebarUpdateCard);
